/**
 * Настройки владельца: S-26 «Себестоимость», S-27 «Себестоимость товара», S-28 «Расходы и налог»,
 * S-25 «Уведомления» (у каждого свои), S-40 «Замер времени», S-06 «Ключ Wildberries»,
 * S-29 «Обслуживание». Пустое поле — «не задано», а не ноль; деньги — строками.
 */
import { useEffect, useRef, useState } from 'react'
import {
  fetchCosts, importCosts, fetchFinanceSettings, saveFinanceSettings, fetchNotify, saveNotify, saveEvening, setSignalRule,
  fetchTime, saveTime, fetchKeyInfo, revokeKey, fetchOpsPanel, type FinanceSettings,
} from '../api/sections.js'
import { backToChat, hapticSuccess } from '../telegram/call.js'
import { Icon } from '../components/icons/Icon.js'
import { Empty, Failed, InlineError, Loading, NoShop, useAnnounce, useData, useScreenHead } from './common.js'
import { Fold, Kv, Sect, ToneTag } from './ui.js'
import type { ScreenId } from '../router/screens.js'

type Open = (id: ScreenId, param?: string) => void

/** Поле формы: подпись слева, поле справа, подсказка ниже. */
function Field({ id, label, hint, children }: { id: string; label: string; hint?: string | null; children: React.ReactNode }): React.JSX.Element {
  return (
    <div className="row row-form">
      <span className="row-main">
        <label className="row-title" htmlFor={id}>{label}</label>
        {hint != null && hint !== '' && <span className="row-meta" id={id + '-hint'}>{hint}</span>}
      </span>
      {children}
    </div>
  )
}

/** Копейки из строки «412,50»; не число — null. Только для подписи «итого» до сохранения ([UI-7.4.18]). */
const kop = (s: string): number | null => {
  const t = s.replace(/\s/g, '').replace(',', '.')
  if (t === '') return 0
  if (!/^[0-9]{1,9}(\.[0-9]{1,2})?$/.test(t)) return null
  const [i = '0', f = ''] = t.split('.')
  return Number(i) * 100 + Number((f + '00').slice(0, 2))
}
const rubOfKop = (k: number): string => {
  const r = Math.floor(k / 100), c = k % 100
  const g = String(r).replace(/\B(?=(\d{3})+(?!\d))/g, String.fromCharCode(160))
  return g + (c === 0 ? '' : ',' + String(c).padStart(2, '0')) + String.fromCharCode(160) + '₽'
}

// ── S-26 Себестоимость ───────────────────────────────────────────────────────

export function ScreenCosts({ open }: { open: Open }): React.JSX.Element {
  const { s, reload } = useData(fetchCosts)
  const [f, setF] = useState<'all' | 'empty' | 'done'>('all')
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<{ summary: string; issues: string[] } | null>(null)
  const announce = useAnnounce()
  const file = useRef<HTMLInputElement>(null)
  useScreenHead(undefined, s.kind === 'ok' && s.data.shop !== null ? `посчитано ${s.data.calc.filled} из ${s.data.calc.total}` : null)
  if (s.kind === 'loading') return <Loading label="Загрузка себестоимости" rows={6} />
  if (s.kind === 'fail') return <Failed message={s.message} onRetry={reload} />
  const d = s.data
  if (d.shop === null) return <NoShop reason={d.reason} />
  const items = d.items.filter((i) => f === 'all' || (f === 'empty' ? i.profit === null : i.profit !== null))
  const onFile = async (fl: File): Promise<void> => {
    setBusy(true)
    // Excel сохраняет CSV в Windows-1251: если UTF-8 даёт «�», читаем как 1251.
    const buf = await fl.arrayBuffer()
    let text = new TextDecoder('utf-8').decode(buf)
    if (text.includes('�')) text = new TextDecoder('windows-1251').decode(buf)
    const r = await importCosts(text)
    setBusy(false)
    if (!r.ok) { announce({ kind: 'fail', text: r.error.message }); return }
    hapticSuccess()
    setResult({ summary: r.data.summary, issues: [...r.data.not_found.map((x) => `не найден артикул «${x}»`), ...r.data.skipped] })
    reload()
  }
  const seg = (k: typeof f, label: string, n: number): React.JSX.Element => (
    <button type="button" className={'seg' + (f === k ? ' seg-on' : '')} aria-pressed={f === k} onClick={() => setF(k)}>{label} <span className="seg-n">{n}</span></button>
  )
  return (
    <div data-testid="screen-body">
      <section className="card card-pad">
        <div className="meter-line" role="img" aria-label={`Калькулятор заполнен у ${d.calc.filled} из ${d.calc.total}`}>
          <span style={{ width: `${d.calc.total === 0 ? 0 : Math.round((100 * d.calc.filled) / d.calc.total)}%` }} />
        </div>
        <p className="body-text gap-top-s">Калькулятор заполнен у {d.calc.filled} из {d.calc.total} моделей. Откройте модель: цена и удержания WB уже подставлены из отчётов, введите себестоимость и расходы — прибыль со штуки сохранится, и по ней Штаб считает прибыль недели.</p>
      </section>
      <div className="segs gap-top">
        {seg('all', 'Все', d.items.length)}
        {seg('empty', 'Не посчитаны', d.items.filter((i) => i.profit === null).length)}
        {seg('done', 'Посчитаны', d.calc.filled)}
      </div>
      <section className="card gap-top" data-testid="cost-list">
        {items.length === 0 && <div className="empty-card"><Empty icon="section-cost" title="Таких товаров нет" text="Смените фильтр." /></div>}
        {items.map((i) => (
          <button type="button" className="row row-tap" key={i.nm_id} data-testid={'cost-' + i.nm_id} onClick={() => open('S-27', String(i.nm_id))}>
            <span className="row-main">
              <span className="row-title">{i.title}</span>
              <span className="row-meta">{[i.vendor_code, `Артикул WB ${i.nm_id}`].filter(Boolean).join(' · ')}</span>
            </span>
            <span className="row-val">
              <span className="num">{i.profit === null ? 'не посчитана' : `${i.profit} со шт.`}</span>
              <span className="num-sub">{i.margin !== null ? `маржа ${i.margin}` : i.unit !== null ? `себестоимость ${i.unit}` : 'себестоимость не введена'}</span>
            </span>
            <Icon name="nav-next" className="ic-chev row-end" />
          </button>
        ))}
      </section>

      <Sect title="Загрузить файлом" />
      <section className="card card-pad">
        <p className="body-text">Таблица CSV из двух колонок: артикул (WB или продавца) и себестоимость единицы в рублях. Первая строка может быть заголовком.</p>
        <input ref={file} type="file" className="vh" accept=".csv,.txt,text/csv,text/plain,application/vnd.ms-excel,application/octet-stream"
          data-testid="cost-file" onChange={(e) => { const fl = e.target.files?.[0]; e.target.value = ''; if (fl) void onFile(fl) }} />
        <button type="button" className="btn btn-soft btn-block gap-top-s" disabled={busy} data-testid="cost-import" onClick={() => file.current?.click()}>
          {busy ? 'Загружаю…' : 'Выбрать файл'}
        </button>
        {result !== null && (
          <div className="gap-top-s" data-testid="cost-import-result">
            <p className="body-text"><b>{result.summary}.</b></p>
            {result.issues.slice(0, 8).map((x, k) => <p className="row-meta" key={k}>{x}</p>)}
          </div>
        )}
      </section>
    </div>
  )
}

// ── S-28 Расходы, налог, пополнение ──────────────────────────────────────────

export function ScreenFinanceSettings(): React.JSX.Element {
  const { s, reload } = useData(fetchFinanceSettings)
  useScreenHead(undefined, null)
  const announce = useAnnounce()
  const [v, setV] = useState<FinanceSettings | null>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  useEffect(() => { if (s.kind === 'ok' && s.data.shop !== null) setV(s.data) }, [s])
  if (s.kind === 'loading' || (s.kind === 'ok' && s.data.shop !== null && v === null)) return <Loading label="Загрузка настроек" rows={6} />
  if (s.kind === 'fail') return <Failed message={s.message} onRetry={reload} />
  if (v === null) return <NoShop reason={s.data.reason} />
  const set = <K extends 'tax' | 'fixed' | 'replenishment' | 'thresholds'>(k: K, patch: Partial<FinanceSettings[K]>): void =>
    setV({ ...v, [k]: { ...v[k], ...patch } })
  const save = async (): Promise<void> => {
    setBusy(true); setErr(null)
    const r = await saveFinanceSettings({ tax: v.tax, fixed: v.fixed, replenishment: v.replenishment,
      thresholds: { low: v.thresholds.low, high: v.thresholds.high, target: v.thresholds.target } })
    setBusy(false)
    if (!r.ok) { setErr(r.error.message); return }
    hapticSuccess()
    announce({ kind: 'ok', text: 'Сохранено. Пересчитаем маржу и запас в следующем отчёте.' })
  }
  const num = (id: string, value: string, on: (x: string) => void, mode: 'decimal' | 'numeric' = 'decimal'): React.JSX.Element => (
    <input id={id} className="inp" inputMode={mode} autoComplete="off" value={value} data-testid={id} onChange={(e) => on(e.target.value)} />
  )
  return (
    <div data-testid="screen-body">
      <Sect title="Налог" />
      <section className="card">
        <Field id="tax-regime" label="Режим">
          <select id="tax-regime" className="inp inp-wide" value={v.tax.regime} data-testid="tax-regime" onChange={(e) => set('tax', { regime: e.target.value, rate: v.limits[e.target.value] === undefined ? v.tax.rate : String(v.limits[e.target.value]!.rate), base: v.limits[e.target.value]?.kind === 'profit' ? 'sale' : v.tax.base || 'sale' })}>
            <option value="">не задан</option>
            {Object.entries(v.regimes).map(([k, t]) => <option key={k} value={k}>{t}</option>)}
          </select>
        </Field>
        {(v.limits[v.tax.regime]?.kind ?? 'income') === 'income' && (
          <Field id="tax-base" label="С чего платится" hint="Базу выбираете вы: Штаб её не угадывает">
            <select id="tax-base" className="inp inp-wide" value={v.tax.base} data-testid="tax-base" onChange={(e) => set('tax', { base: e.target.value })}>
              <option value="">не выбрано</option>
              {Object.entries(v.bases).map(([k, t]) => <option key={k} value={k}>{t}</option>)}
            </select>
          </Field>
        )}
        <Field id="tax-rate" label="Ставка, %" hint={v.limits[v.tax.regime] === undefined ? null
          : v.limits[v.tax.regime]!.min === v.limits[v.tax.regime]!.max ? `для этого режима — ${v.limits[v.tax.regime]!.min} %`
          : `для этого режима — от ${v.limits[v.tax.regime]!.min} до ${v.limits[v.tax.regime]!.max} %`}>{num('tax-rate', v.tax.rate, (x) => set('tax', { rate: x }))}</Field>
        <Field id="tax-vat" label="НДС" hint="с 2026 года УСН при доходе больше 20 млн ₽ платит НДС: 5 %, 7 % или 22 %">
          <select id="tax-vat" className="inp inp-wide" value={v.tax.vat} data-testid="tax-vat" onChange={(e) => set('tax', { vat: e.target.value })}>
            {v.vat_rates.map((x) => <option key={x} value={String(x)}>{x === 0 ? 'не плачу' : `${x} %`}</option>)}
          </select>
        </Field>
      </section>

      <Sect title="Постоянные расходы" />
      <section className="card">
        <Field id="fixed-monthly" label="В месяц, ₽" hint="аренда, зарплаты, сервисы — всё, что не зависит от продаж">
          {num('fixed-monthly', v.fixed.monthly, (x) => set('fixed', { monthly: x }))}
        </Field>
        <Field id="fixed-alloc" label="Делить по товарам">
          <select id="fixed-alloc" className="inp inp-wide" value={v.fixed.allocation} onChange={(e) => set('fixed', { allocation: e.target.value })}>
            <option value="by_revenue">по выручке</option>
            <option value="by_units">по проданным штукам</option>
            <option value="none">не делить</option>
          </select>
        </Field>
      </section>

      <Sect title="Пополнение" />
      <section className="card">
        <Field id="rep-lead" label="Срок поставки, дн." hint="от заказа у поставщика до приёмки на складе">
          {num('rep-lead', v.replenishment.lead_time, (x) => set('replenishment', { lead_time: x }), 'numeric')}
        </Field>
        <Field id="rep-safety" label="Страховой запас, шт.">{num('rep-safety', v.replenishment.safety, (x) => set('replenishment', { safety: x }), 'numeric')}</Field>
        <Field id="rep-drr" label="Целевой ДРР, %" hint="по заказам из рекламы">{num('rep-drr', v.replenishment.target_drr, (x) => set('replenishment', { target_drr: x }))}</Field>
        <Field id="th-target" label="Целевой запас, дн.">{num('th-target', v.thresholds.target, (x) => set('thresholds', { target: x }), 'numeric')}</Field>
        <Field id="th-low" label="«Мало запаса» — меньше, дн." hint={`по умолчанию ${v.thresholds.defaults.low}`}>
          {num('th-low', v.thresholds.low, (x) => set('thresholds', { low: x }), 'numeric')}
        </Field>
        <Field id="th-high" label="«Залежался» — больше, дн." hint={`по умолчанию ${v.thresholds.defaults.high}`}>
          {num('th-high', v.thresholds.high, (x) => set('thresholds', { high: x }), 'numeric')}
        </Field>
      </section>
      <p className="hint">Значения по умолчанию. Это не норма Wildberries, а настройка под ваш цикл пополнения.</p>
      {err !== null && <InlineError text={err} testid="save-msg" />}
      <div className="form-actions">
        <button type="button" className="btn btn-block" disabled={busy} data-testid="fin-save" onClick={() => { void save() }}>{busy ? 'Сохраняю…' : 'Сохранить'}</button>
      </div>
    </div>
  )
}

// ── S-25 Уведомления ─────────────────────────────────────────────────────────

export function ScreenNotify({ open }: { open: Open }): React.JSX.Element {
  const { s, reload } = useData(fetchNotify)
  useScreenHead(undefined, null)
  const announce = useAnnounce()
  const [v, setV] = useState<{ enabled: boolean; quiet_from: string; quiet_to: string; ev_on: boolean; ev_time: string } | null>(null)
  const [rules, setRules] = useState<Record<string, boolean>>({})
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  useEffect(() => {
    if (s.kind !== 'ok' || s.data.shop === null) return
    setV({ enabled: s.data.enabled, quiet_from: s.data.quiet_from, quiet_to: s.data.quiet_to, ev_on: s.data.evening.on, ev_time: s.data.evening.time })
    setRules(Object.fromEntries(s.data.signals.map((x) => [x.code, x.on])))
  }, [s])
  if (s.kind === 'loading' || (s.kind === 'ok' && s.data.shop !== null && v === null)) return <Loading label="Загрузка настроек" rows={4} />
  if (s.kind === 'fail') return <Failed message={s.message} onRetry={reload} />
  const d = s.data
  if (d.shop === null || v === null) return <NoShop reason={d.reason} />
  const save = async (): Promise<void> => {
    setBusy(true); setErr(null)
    const r = await saveNotify({ enabled: v.enabled, quiet_from: v.quiet_from, quiet_to: v.quiet_to })
    const e = r.ok ? await saveEvening({ on: v.ev_on, time: v.ev_time }) : r
    setBusy(false)
    if (!r.ok) { setErr(r.error.message); return }
    if (!e.ok) { setErr(e.error.message); return }
    hapticSuccess()
    announce({ kind: 'ok', text: 'Сохранено' })
  }
  // Переключатель правила сохраняется сразу — как права сотрудника: это решение, а не черновик формы.
  const toggle = async (code: string, name: string): Promise<void> => {
    const on = !(rules[code] ?? true)
    setRules({ ...rules, [code]: on })
    const r = await setSignalRule(code, on)
    if (!r.ok) { setRules({ ...rules, [code]: !on }); announce({ kind: 'fail', text: r.error.message }); return }
    hapticSuccess(); announce({ kind: 'ok', text: on ? `Включено: ${name.toLowerCase()}` : `Выключено: ${name.toLowerCase()}` })
  }
  return (
    <div data-testid="screen-body">
      <section className="card">
        <div className="row row-form">
          <span className="row-main">
            <span className="row-title" id="nt-on-l">Присылать отчёты и сигналы в Telegram</span>
            <span className="row-meta">{d.gets_reports ? 'утренние отчёты, сигналы и вечерняя сводка' : 'сигналы и вечерняя сводка; отчётов открытых вам разделов сейчас нет'}. Срочное — ключ WB и деньги на рекламу — приходит всегда</span>
          </span>
          <button type="button" role="switch" aria-checked={v.enabled} aria-labelledby="nt-on-l" className={'switch' + (v.enabled ? ' switch-on' : '')}
            data-testid="nt-enabled" onClick={() => setV({ ...v, enabled: !v.enabled })}><span className="switch-knob" /></button>
        </div>
      </section>

      <Sect title="Тихие часы" aside="время магазина" />
      <section className="card">
        <Field id="nt-from" label="С"><input id="nt-from" className="inp" type="time" value={v.quiet_from} data-testid="nt-from" onChange={(e) => setV({ ...v, quiet_from: e.target.value })} /></Field>
        <Field id="nt-to" label="До"><input id="nt-to" className="inp" type="time" value={v.quiet_to} data-testid="nt-to" onChange={(e) => setV({ ...v, quiet_to: e.target.value })} /></Field>
      </section>
      <p className="hint">В тихие часы отчёт ждёт их конца, а сигналы копятся и приходят утром одним списком «Пока вы спали»; что успело уладиться, придёт строкой «снято». Срочное приходит сразу. Отчёт, опоздавший больше чем на 3 часа, не досылается. Тихие часы у каждого свои.</p>

      <Sect title="Вечерняя сводка" />
      <section className="card">
        <div className="row row-form">
          <span className="row-main">
            <span className="row-title" id="nt-ev-l">Присылать вечернюю сводку</span>
            <span className="row-meta">то, что не приходило отдельно, и снятые за день сигналы; пустая не приходит</span>
          </span>
          <button type="button" role="switch" aria-checked={v.ev_on} aria-labelledby="nt-ev-l" className={'switch' + (v.ev_on ? ' switch-on' : '')}
            data-testid="nt-ev" onClick={() => setV({ ...v, ev_on: !v.ev_on })}><span className="switch-knob" /></button>
        </div>
        {v.ev_on && <Field id="nt-ev-time" label="Время"><input id="nt-ev-time" className="inp" type="time" value={v.ev_time} data-testid="nt-ev-time" onChange={(e) => setV({ ...v, ev_time: e.target.value })} /></Field>}
      </section>

      {d.signals.length > 0 && <>
        <Sect title="Сигналы" aside={`${Object.values(rules).filter(Boolean).length} из ${d.signals.length}`} />
        <section className="card" data-testid="nt-signals">
          {d.signals.map((x, k) => (<div key={x.code}>
            {(k === 0 || d.signals[k - 1]!.section !== x.section) && <p className="row-meta sig-section">{x.section}</p>}
            <div className="row row-form">
              <span className="row-main">
                <span className="row-title" id={'sg-' + x.code}>{x.name}</span>
                <span className="row-meta">{x.about}. Приходит {x.when}</span>
              </span>
              <button type="button" role="switch" aria-checked={rules[x.code] ?? true} aria-labelledby={'sg-' + x.code}
                className={'switch' + ((rules[x.code] ?? true) ? ' switch-on' : '')} data-testid={'sg-' + x.code}
                onClick={() => { void toggle(x.code, x.name) }}><span className="switch-knob" /></button>
            </div>
          </div>))}
        </section>
        <p className="hint">Сигналы считает программа по данным кабинета, без ИИ. Повтор по тому же товару — не чаще раза в сутки; больше трёх одинаковых сразу — одним сообщением; больше 20 в день — остальное в вечернюю сводку. Переключатели сохраняются сразу и действуют только для вас.</p>
      </>}

      {d.reports.length > 0 && <>
        <Sect title="Когда приходят отчёты" />
        <section className="card">
          <Kv rows={d.reports.map((r) => ({ label: r.title, text: r.time }))} />
          <button type="button" className="row row-tap row-link has-ic" data-testid="nt-agents" onClick={() => open('S-23')}>
            <Icon name="section-agents" className="ic-action" />
            <span className="row-main"><span className="row-title">Изменить время отчётов</span></span>
            <Icon name="nav-next" className="ic-chev row-end" />
          </button>
        </section>
        <p className="hint">Отчёт считается ночью и доставляется в указанное время.</p>
      </>}

      {d.recent.length > 0 && <>
        <Sect title="Последние сигналы" />
        <section className="card" data-testid="nt-recent">
          {d.recent.map((x, k) => (
            <div className="row" key={k}>
              <span className="row-main">
                <span className="row-title">{x.title}</span>
                <span className="row-meta">{[x.subject, x.at, x.status].filter(Boolean).join(' · ')}</span>
              </span>
            </div>
          ))}
        </section>
      </>}
      {err !== null && <InlineError text={err} testid="save-msg" />}
      <div className="form-actions">
        <button type="button" className="btn btn-block" disabled={busy} data-testid="nt-save" onClick={() => { void save() }}>{busy ? 'Сохраняю…' : 'Сохранить'}</button>
      </div>
    </div>
  )
}

// ── S-40 Замер времени ───────────────────────────────────────────────────────

export function ScreenTime({ open }: { open: Open }): React.JSX.Element {
  const { s, reload } = useData(fetchTime)
  useScreenHead('Сколько времени уходит на кабинет', null)
  const announce = useAnnounce()
  const [form, setForm] = useState(false)
  const [v, setV] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  if (s.kind === 'loading') return <Loading label="Загрузка замеров" rows={5} />
  if (s.kind === 'fail') return <Failed message={s.message} onRetry={reload} />
  const d = s.data
  if (d.shop === null) return <NoShop reason={d.reason} />
  const filled = Object.values(v).filter((x) => x.trim() !== '')
  const total = filled.every((x) => /^[0-9]{1,4}$/.test(x.trim())) ? filled.reduce((a, x) => a + Number(x), 0) : null
  const save = async (): Promise<void> => {
    setBusy(true); setErr(null)
    const r = await saveTime(v)
    setBusy(false)
    if (!r.ok) { setErr(r.error.message); return }
    hapticSuccess()
    announce({ kind: 'ok', text: r.data.kind === 'baseline' ? 'Замер сохранён. Через месяц сравним.' : 'Текущий замер сохранён.' })
    setForm(false); setV({}); reload()
  }
  if (!form && d.history.length > 0) {
    return (
      <div data-testid="screen-body">
        <section className="card">
          <Kv rows={[{ label: 'Экономия времени', text: d.saved.display, sub: d.saved.display === null ? d.saved.reason : null, strong: true }]} />
        </section>
        <Sect title="Замеры" />
        <section className="card"><Kv rows={d.history.map((h) => ({ label: `${h.kind} · ${h.date}`, text: h.total, sub: h.ops }))} /></section>
        <div className="form-actions">
          <button type="button" className="btn btn-block" data-testid="time-new" onClick={() => setForm(true)}>Сделать новый замер</button>
        </div>
        <p className="hint">Базовый замер не перезаписывается: новый пишется как текущий, и экономия считается как разница.</p>
      </div>
    )
  }
  return (
    <div data-testid="screen-body">
      <p className="lead-note">Отметьте по каждой операции, сколько минут в день она занимает. Это точка отсчёта: через месяц сравним и увидим, сколько времени вернулось.</p>
      <section className="card">
        {d.operations.map((op) => (
          <Field key={op.code} id={'tm-' + op.code} label={op.title} hint={op.where}>
            <input id={'tm-' + op.code} className="inp inp-short" inputMode="numeric" autoComplete="off" value={v[op.code] ?? ''}
              aria-label={`${op.title}, минут в день`} data-testid={'tm-' + op.code}
              onChange={(e) => setV({ ...v, [op.code]: e.target.value })} />
          </Field>
        ))}
      </section>
      <p className="hint">Пустое поле — это «не знаю», а не ноль. Пустые операции в счёт не идут. Замер сохраняется с датой — {d.today_display}.</p>
      {filled.length > 0 && total !== null && <p className="body-text gap-top-s" data-testid="time-total">Сейчас на обход кабинета уходит {total} мин в день.</p>}
      {err !== null && <InlineError text={err} testid="save-msg" />}
      <div className="form-actions">
        <button type="button" className="btn btn-block" disabled={busy || filled.length === 0} data-testid="time-save" onClick={() => { void save() }}>
          {busy ? 'Сохраняю…' : 'Сохранить замер'}
        </button>
      </div>
      {!d.has_baseline && (
        <button type="button" className="link-inline" data-testid="time-skip" onClick={() => open('S-07')}>
          Пропустить — без замера экономию времени посчитать не из чего
        </button>
      )}
    </div>
  )
}

// ── S-06 Ключ Wildberries ────────────────────────────────────────────────────

export function ScreenKey(): React.JSX.Element {
  const { s, reload } = useData(fetchKeyInfo)
  useScreenHead(undefined, null)
  const announce = useAnnounce()
  const [word, setWord] = useState('')
  const [busy, setBusy] = useState(false)
  if (s.kind === 'loading') return <Loading label="Загрузка ключа" rows={5} />
  if (s.kind === 'fail') return <Failed message={s.message} onRetry={reload} />
  const d = s.data
  if (d.shop === null) return <NoShop reason={d.reason} />
  const t = d.token
  const revoke = async (): Promise<void> => {
    setBusy(true)
    const r = await revokeKey(word)
    setBusy(false)
    if (!r.ok) { announce({ kind: 'fail', text: r.error.message }); return }
    announce({ kind: 'ok', text: 'Ключ отозван. Загрузка данных остановлена.' })
    setWord(''); reload()
  }
  return (
    <div data-testid="screen-body">
      {t === null
        ? <section className="card empty-card"><Empty icon="section-key" title="Ключ не подключён" text="Пришлите ключ Wildberries боту сообщением — он проверит его и удалит сообщение из чата." /></section>
        : <>
            <section className="card card-pad">
              <p className="fb-head"><ToneTag tone={t.state.tone} word={t.state.word} /></p>
              {t.expiring_note !== null && <p className="body-text gap-top-s">{t.expiring_note}</p>}
            </section>
            <section className="card gap-top">
              <Kv testid="key-info" rows={[
                { label: 'Действует до', text: t.expires_display },
                { label: 'Тип', text: t.kind },
                { label: 'Только чтение', text: t.read_only ? 'да' : 'нет', sub: t.read_only ? null : 'Штаб всё равно только читает кабинет' },
                { label: 'Подключён', text: t.connected_display },
              ]} />
            </section>
            <Sect title="Доступы ключа" aside={t.missing.length > 0 ? `не хватает ${t.missing.length}` : 'все нужные есть'} />
            <section className="card card-pad">
              <div className="chips">
                {t.categories.map((c) => (
                  <span key={c.code} className={'chip' + (c.on ? ' chip-on' : c.required ? ' chip-miss' : '')}>
                    {c.on ? <Icon name="action-confirm" size={16} /> : null}{c.title}
                    {!c.on && c.required ? <span className="vh"> — нужен, не выдан</span> : null}
                  </span>
                ))}
              </div>
              {t.missing.length > 0 && <p className="row-meta gap-top-s">Не хватает: {t.missing.join(', ')}. Создайте новый ключ с этими категориями.</p>}
            </section>
          </>}
      {d.sync.length > 0 && <>
        <Sect title="Последняя загрузка данных" />
        <section className="card"><Kv rows={d.sync.map((x) => ({ label: x.title, text: x.at ?? 'ещё не было', sub: x.ok ? null : 'последняя попытка не удалась' }))} /></section>
      </>}
      <Sect title="Что можно сделать" />
      <section className="card">
        <Fold q="Заменить ключ" testid="key-replace">
          <p>Создайте новый токен в кабинете Wildberries: Профиль → Настройки → Доступ к API, тип «Персональный», нужные категории — на экране выше.</p>
          <p>Пришлите его боту сообщением: бот проверит ключ, заменит старый и удалит сообщение из чата.</p>
          <button type="button" className="btn btn-soft" data-testid="key-to-chat" onClick={() => { if (!backToChat()) announce({ kind: 'info', text: 'Откройте чат с ботом в Telegram.' }) }}>Перейти в чат с ботом</button>
        </Fold>
        {t !== null && t.state.word !== 'Отозван' && (
          <Fold q="Отозвать ключ" testid="key-revoke">
            <p>{d.revoke_note}</p>
            <label className="row-meta" htmlFor="key-word">Чтобы подтвердить, напишите слово «отозвать»</label>
            <input id="key-word" className="inp inp-wide" autoComplete="off" value={word} data-testid="key-word" onChange={(e) => setWord(e.target.value)} />
            <button type="button" className="btn btn-danger" disabled={busy || word.trim().toLowerCase() !== 'отозвать'} data-testid="key-revoke-btn" onClick={() => { void revoke() }}>
              Отозвать ключ
            </button>
          </Fold>
        )}
      </section>
    </div>
  )
}

// ── S-29 Обслуживание ────────────────────────────────────────────────────────

export function ScreenOps(): React.JSX.Element {
  const { s, reload } = useData(fetchOpsPanel)
  useScreenHead(undefined, 'служебная сводка')
  if (s.kind === 'loading') return <Loading label="Загрузка панели" rows={6} />
  if (s.kind === 'fail') return <Failed message={s.message} onRetry={reload} />
  const d = s.data
  if (d.shop === null) return <NoShop reason={d.reason} />
  return (
    <div data-testid="screen-body">
      <section className="card">
        {d.sections.map((x) => (
          <Fold key={x.code} testid={'ops-' + x.code}
            q={<span className="ops-q">{x.title}<ToneTag tone={x.ok ? 'good' : 'attn'} word={x.ok ? 'в порядке' : 'есть сбои'} /></span>}>
            {x.rows.length === 0 ? <p>Записей нет.</p> : <Kv rows={x.rows.map((r) => ({ label: r.title, text: r.value, sub: r.sub }))} />}
          </Fold>
        ))}
      </section>
      <button type="button" className="btn btn-soft btn-block gap-top" onClick={reload}><Icon name="action-retry" size={20} />Обновить</button>
    </div>
  )
}
